import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getMyRegistrations, getMyClasses } from '../services/users';
import { cancelRegistration } from '../services/classes';
import {
  MyRegistrationsData, DashboardClass, RegistrationWithClass, ClassStatus,
} from '../types/models';

type Tab = 'classes' | 'created';

const STATUS_COLORS: Record<ClassStatus, string> = {
  Active:    'bg-green-100 text-green-800',
  Planned:   'bg-blue-100 text-blue-800',
  Completed: 'bg-gray-100 text-gray-600',
  Cancelled: 'bg-red-100 text-red-800',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ── Registration card (upcoming / past) ───────────────────────────────────────

interface RegCardProps {
  entry: RegistrationWithClass;
  isUpcoming: boolean;
  onCancel?: (eventId: string) => Promise<void>;
}

const RegCard: React.FC<RegCardProps> = ({ entry, isUpcoming, onCancel }) => {
  const [cancelling, setCancelling] = useState(false);
  const { event } = entry;

  const handleCancel = async () => {
    if (!onCancel) return;
    setCancelling(true);
    try {
      await onCancel(event.eventId);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
      <div className="flex-1 min-w-0">
        <Link
          to={`/classes/${event.eventId}`}
          className="text-base font-semibold text-gray-900 hover:text-green-700 transition-colors"
        >
          {event.title}
        </Link>
        <p className="text-sm text-gray-500 mt-0.5">{formatDate(event.startDateTime)}</p>
        <p className="text-sm text-gray-500">{event.physicalAddress.city}, {event.physicalAddress.state}</p>
        <p className="text-xs text-gray-400 mt-1">{event.classType} · {event.duration} min</p>
      </div>
      {isUpcoming && onCancel && (
        <button
          onClick={handleCancel}
          disabled={cancelling}
          className="shrink-0 text-sm text-red-500 hover:text-red-700 border border-red-200 hover:border-red-400 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
        >
          {cancelling ? 'Cancelling…' : 'Cancel'}
        </button>
      )}
    </div>
  );
};

// ── Waitlist card ─────────────────────────────────────────────────────────────

const WaitlistCard: React.FC<{ entry: RegistrationWithClass }> = ({ entry }) => {
  const { registration, event } = entry;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
      <div className="flex-1 min-w-0">
        <Link
          to={`/classes/${event.eventId}`}
          className="text-base font-semibold text-gray-900 hover:text-green-700 transition-colors"
        >
          {event.title}
        </Link>
        <p className="text-sm text-gray-500 mt-0.5">{formatDate(event.startDateTime)}</p>
        <p className="text-sm text-gray-500">{event.physicalAddress.city}, {event.physicalAddress.state}</p>
      </div>
      <span className="shrink-0 self-start bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-1 rounded-full">
        #{registration.waitlistPosition ?? '?'} on waitlist
      </span>
    </div>
  );
};

// ── Capacity bar ──────────────────────────────────────────────────────────────

const CapacityBar: React.FC<{ registered: number; capacity: number }> = ({ registered, capacity }) => {
  const pct = Math.min(100, Math.round((registered / Math.max(1, capacity)) * 100));
  const color = pct >= 90 ? 'bg-red-400' : pct >= 70 ? 'bg-yellow-400' : 'bg-green-400';
  return (
    <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="shrink-0">{registered}/{capacity} registered</span>
    </div>
  );
};

// ── My Created Class card ─────────────────────────────────────────────────────

const MyClassCard: React.FC<{ event: DashboardClass }> = ({ event }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <Link
          to={`/classes/${event.eventId}`}
          className="text-base font-semibold text-gray-900 hover:text-green-700 transition-colors"
        >
          {event.title}
        </Link>
        <p className="text-sm text-gray-500 mt-0.5">{formatDate(event.startDateTime)}</p>
      </div>
      <span className={`shrink-0 text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_COLORS[event.status]}`}>
        {event.status}
      </span>
    </div>

    <CapacityBar registered={event.registeredCount} capacity={event.capacity} />

    <div className="flex gap-4 mt-3 text-sm">
      <Link to={`/classes/${event.eventId}`} className="text-green-700 hover:text-green-800 font-medium">
        View
      </Link>
      {(event.status === 'Planned' || event.status === 'Active') && (
        <Link to={`/classes/${event.eventId}/edit`} className="text-gray-600 hover:text-gray-800 font-medium">
          Edit
        </Link>
      )}
    </div>
  </div>
);

// ── Dashboard page ────────────────────────────────────────────────────────────

export const DashboardPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState<Tab>('classes');
  const [regData, setRegData] = useState<MyRegistrationsData | null>(null);
  const [myClasses, setMyClasses] = useState<DashboardClass[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isCreator = currentUser?.roles.some(r => r === 'Teacher' || r === 'Organizer' || r === 'Admin') ?? false;

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [regs, classesResult] = await Promise.all([
        getMyRegistrations(),
        isCreator ? getMyClasses() : Promise.resolve(null),
      ]);
      setRegData(regs);
      setMyClasses(classesResult?.classes ?? null);
    } catch {
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isCreator]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCancelRegistration = async (eventId: string) => {
    await cancelRegistration(eventId);
    await loadData();
  };

  const sectionLabel = 'text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3';
  const emptyMsg = 'text-sm text-gray-400 py-6 text-center';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-7">
        <TabButton active={tab === 'classes'} onClick={() => setTab('classes')}>My Classes</TabButton>
        {isCreator && (
          <TabButton active={tab === 'created'} onClick={() => setTab('created')}>My Created Classes</TabButton>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={loadData} className="ml-4 underline font-medium">Retry</button>
        </div>
      )}

      {/* My Classes tab */}
      {!loading && !error && tab === 'classes' && regData && (
        <div className="space-y-10">
          <section>
            <h2 className={sectionLabel}>Upcoming ({regData.upcoming.length})</h2>
            {regData.upcoming.length === 0 ? (
              <p className={emptyMsg}>
                No upcoming registrations.{' '}
                <Link to="/classes" className="text-green-700 underline">Browse classes</Link>
              </p>
            ) : (
              <div className="space-y-3">
                {regData.upcoming.map(e => (
                  <RegCard key={e.event.eventId} entry={e} isUpcoming onCancel={handleCancelRegistration} />
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className={sectionLabel}>Waitlisted ({regData.waitlisted.length})</h2>
            {regData.waitlisted.length === 0 ? (
              <p className={emptyMsg}>You are not on any waitlists.</p>
            ) : (
              <div className="space-y-3">
                {regData.waitlisted.map(e => (
                  <WaitlistCard key={e.event.eventId} entry={e} />
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className={sectionLabel}>Past ({regData.past.length})</h2>
            {regData.past.length === 0 ? (
              <p className={emptyMsg}>No past classes yet.</p>
            ) : (
              <div className="space-y-3">
                {regData.past.map(e => (
                  <RegCard key={e.event.eventId} entry={e} isUpcoming={false} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* My Created Classes tab */}
      {!loading && !error && tab === 'created' && myClasses && (
        <div>
          {myClasses.length === 0 ? (
            <p className={emptyMsg}>
              You have not created any classes.{' '}
              <Link to="/classes/create" className="text-green-700 underline">Create one</Link>
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {myClasses.map(e => <MyClassCard key={e.eventId} event={e} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active, onClick, children,
}) => (
  <button
    className={`pb-3 mr-7 text-sm font-medium border-b-2 transition-colors ${
      active
        ? 'border-green-600 text-green-700'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
    onClick={onClick}
  >
    {children}
  </button>
);

export default DashboardPage;
